import { UnitOverviewFilterInput } from "graphqlBase/types";
import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { unitsOverviewFilterAtom } from "../State/manageFiltersInUnitsOverview";

const deepmergeSave = (
  a: { filters: UnitOverviewFilterInput },
  b: { filters: UnitOverviewFilterInput }
): { filters: UnitOverviewFilterInput } => {
  const existing =
    (a.filters as { and?: UnitOverviewFilterInput[] }).and ?? (Object.keys(a.filters).length > 0 ? [a.filters] : []);
  return { filters: { and: [...existing, b.filters] } };
};

type FilterBuilderVariables = {
  filters: UnitOverviewFilterInput;
};
export const useFilterBuilder = () => {
  const filterValues = useRecoilValue(unitsOverviewFilterAtom);
  const filterValuesForCustomizedFilters = useMemo(
    () =>
      Object.entries(filterValues).reduce<FilterBuilderVariables>(
        (filters, [key, filterValue]) => {
          if (!filterValue) return filters;
          if (filterValue.length > 0 && key === "documentType") {
            const documentTypeFilter = {
              certificateTypeId: {
                in: filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: documentTypeFilter });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "primaryEnergySource") {
            const primaryEnergySourceFilter = {
              primaryEnergySource: {
                in: filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: primaryEnergySourceFilter });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "categoryClassEquipment") {
            const categoryClassEquipmentFilter = {
              category: {
                in: filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: categoryClassEquipmentFilter });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "certificateValidityStatus") {
            const certificateValidityStatus = {
              certificateValidityStatusId: {
                in: filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: certificateValidityStatus });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "certificateNorms") {
            const certificateNorms = {
              certificateNormIssueDateDescriptions: {
                or: filterValue.map((norm: string) => ({ contains: norm })),
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: certificateNorms });
            return nextFilter;
          }
          if (key === "hasZerezId" && filterValue === true) {
            const hasZerezId = {
              isImported: {
                eq: !filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: hasZerezId });
            return nextFilter;
          }
          if (key === "isVerified" && filterValue === true) {
            const isVerified = {
              isVerified: {
                eq: filterValue,
              },
            };
            const nextFilter = deepmergeSave(filters, { filters: isVerified });
            return nextFilter;
          }
          if (key === "hasMatrixParameters" && filterValue === true) {
            const hasMatrixParameters = {
              or: [{ hasMatrixParameters: { eq: true } }, { hasInertiaParameters: { eq: true } }],
            };
            const nextFilter = deepmergeSave(filters, { filters: hasMatrixParameters });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "subscribed" && filterValue) {
            const isSubscribed = {
              unitId: {
                in: filterValue,
              },
            };

            // const nextFilter = deepmergeSave(filters, filters);
            const nextFilter = deepmergeSave(filters, { filters: isSubscribed });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "certificateHolderId") {
            const manufacturer = {
              certificateHolderId: {
                in: filterValue,
              },
            };

            const nextFilter = deepmergeSave(filters, { filters: manufacturer });
            return nextFilter;
          }
          if (filterValue.length > 0 && key === "certificateAuthorityId") {
            const authority = {
              certificateAuthorityId: {
                in: filterValue,
              },
            };

            const nextFilter = deepmergeSave(filters, { filters: authority });
            return nextFilter;
          }
          if (filterValue && key === "tenantId") {
            const tenantId = {
              or: [
                {
                  certificateHolderId: {
                    eq: filterValue,
                  },
                },
                {
                  certificateAuthorityId: {
                    eq: filterValue,
                  },
                },
              ],
            };
            const nextFilter = deepmergeSave(filters, { filters: tenantId });
            return nextFilter;
          }
          if (filterValue.length && (key === "ratedVoltage" || key === "maxActivePower")) {
            // Full range = default = no restriction, skip filter entirely
            if (filterValue[0] === 0 && filterValue[1] === Infinity) return filters;
            const nextFilter = deepmergeSave(filters, {
              filters: { [key]: { gte: filterValue[0], lte: filterValue[1] } },
            });
            return nextFilter;
          }
          return filters;
        },
        { filters: {} } as { filters: UnitOverviewFilterInput }
      ),
    [filterValues]
  );
  return filterValuesForCustomizedFilters;
};
